import { useRef } from 'react';
import { Button, useToast } from '../../../../components/ui';
import { cleanStamp, loadImage } from '../SignatureInput/signatureImage';

/**
 * Company stamp printed next to the signature on quotations and invoices.
 * value / onChange: PNG data URL ('' when there is none). The photo's white paper becomes
 * transparent, the empty border is trimmed, and the stamp keeps its ink colour.
 */
export function StampInput({ value, onChange }: { value: string; onChange: (dataUrl: string) => void }) {
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  const pick = async (file?: File) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast.error('Pick an image smaller than 8 MB');
    try {
      const url = cleanStamp(await loadImage(file));
      if (!url) return toast.error('No stamp found in this image. Stamp on plain white paper and take a clear photo.');
      onChange(url);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex h-36 w-36 items-center justify-center rounded-lg border border-line bg-white p-3">
        {value ? (
          <img src={value} alt="Saved company stamp" className="max-h-full max-w-full object-contain" />
        ) : (
          <span className="text-center text-sm text-ink-400">No stamp yet</span>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" leftIcon="download" onClick={() => fileRef.current?.click()}>
          {value ? 'Replace stamp' : 'Upload stamp'}
        </Button>
        {value && <Button size="sm" variant="dangerText" onClick={() => onChange('')}>Remove</Button>}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        hidden
        onChange={(e) => {
          pick(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
    </div>
  );
}

export default StampInput;
